import type {
  GraphEdgeSettingsStyle,
  GroupEdgeSettings,
  PathLookupEdgeSettings,
  TopologyEdgeSettings,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { type FC, memo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import classNames from 'classnames';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import { TopologyLayoutSettingsProtocolsProtocol } from 'components/Graph/GraphSidePanel/TopologyLayoutSettingsProtocolsProtocol/TopologyLayoutSettingsProtocolsProtocol';
import { isTopologyProtocolSettings } from 'components/Graph/GraphState';
import { getEdgeAttribute } from 'components/Graph/helpers/getEdgeAttribute';
import { useLocale } from 'containers/App/LocaleContext';
import type { StpInstancesHook } from '../TopologyLayoutSettingsProtocolsProtocol/useStpInstances';
import { useGroupDragAndDrop } from './useGroupDragAndDrop';

export interface GroupProps {
  closePane?: () => void;
  draggedGroupId: string | null;
  groupId: GroupEdgeSettings['id'] | null;
  groupIndex: number;
  groupGrouped: boolean;
  groupName: GroupEdgeSettings['name'];
  groupVisible: boolean;
  openPane?: (paneContent: ReactNode) => void;
  otherProtocols: boolean;
  protocols: (TopologyEdgeSettings | PathLookupEdgeSettings)[];
  style?: GraphEdgeSettingsStyle;
  stpInstancesHook?: StpInstancesHook;
}

const BareTopologyLayoutSettingsProtocolsGroup: FC<GroupProps> = (props) => {
  const msg = useLocale();
  const groupRef = useRef<HTMLDivElement>(null);
  const [groupHovered, setGroupHovered] = useState<boolean>(false);

  const ctxProps = useGraphContextSelectors({
    preset: prop('layoutSettings', 'presetDraft'),
    dispatch: prop('dispatch'),
    draggedProtocol: prop('layoutSettings', 'draggedProtocol'),
    presetEditMode: prop('layoutSettings', 'presetEditMode'),
  });

  const handleGroupMouseEnter = () => {
    if (ctxProps.presetEditMode) {
      setGroupHovered(true);
    }
  };

  const handleGroupMouseLeave = () => {
    if (ctxProps.presetEditMode) {
      setGroupHovered(false);
    }
  };

  useGroupDragAndDrop({
    dispatch: ctxProps.dispatch,
    presetEditMode: ctxProps.presetEditMode,
    groupRef,
    groupId: props.groupId,
    groupIndex: props.groupIndex,
    groupName: props.groupName,
    otherProtocols: props.otherProtocols,
    protocols: props.protocols,
    style: props.style,
  });

  const groupIsDragged =
    !props.otherProtocols && props.draggedGroupId === props.groupId;
  const emptyGroup = !props.protocols.length;

  if (!ctxProps.presetEditMode && emptyGroup && !props.otherProtocols) {
    return null;
  }

  return (
    <div
      className={classNames(
        !props.otherProtocols &&
          groupHovered &&
          'graph-layout-settings-protocols__protocol--hover',
        groupIsDragged && 'graph-layout-settings-protocols__group--border',
        'relative',
      )}
      ref={groupRef}
    >
      <div className={classNames(groupIsDragged && 'invisible')}>
        <TopologyLayoutSettingsProtocolsProtocol
          borderBottom={!emptyGroup}
          closePane={props.closePane}
          edgeSettingsId={props.groupId}
          disableColor={props.otherProtocols}
          disableControls={props.otherProtocols}
          disableDeleteButton={!ctxProps.presetEditMode}
          disableDrag={props.otherProtocols}
          disableHover={props.otherProtocols}
          childProtocols={props.protocols}
          grouped={props.groupGrouped}
          groupGrouped={false}
          groupId={props.groupId}
          groupIndex={props.groupIndex}
          groupVisible
          inGroup
          onMouseEnter={handleGroupMouseEnter}
          onMouseLeave={handleGroupMouseLeave}
          openPane={props.openPane}
          protocolIndex={-1}
          style={props.style}
          title={props.groupName}
          visible={props.groupVisible}
          stpInstancesHook={props.stpInstancesHook}
        />
        {props.protocols.map((protocol, protocolIndex) => (
          <TopologyLayoutSettingsProtocolsProtocol
            borderBottom={false}
            closePane={props.closePane}
            disableColor={false}
            disableControls={false}
            disableDeleteButton
            disableDrag={false}
            disableHover={false}
            edgeSettingsId={protocol.id}
            groupGrouped={props.groupGrouped}
            groupId={props.groupId}
            groupIndex={props.groupIndex}
            groupVisible={props.groupVisible}
            grouped={protocol.grouped}
            inGroup={false}
            key={protocol.name}
            openPane={props.openPane}
            protocolIndex={protocolIndex}
            protocolName={
              isTopologyProtocolSettings(protocol) ? protocol.name : undefined
            }
            style={protocol.style}
            title={getEdgeAttribute(protocol).name || ''}
            visible={protocol.visible}
            stpInstancesHook={props.stpInstancesHook}
          />
        ))}
        {emptyGroup &&
          (ctxProps.presetEditMode ? (
            <div className='graph-layout-settings-protocol-empty-group__wrapper mx-5'>
              <div className='graph-layout-settings-protocol-empty-group__boxes relative flex-1'>
                <div className='graph-layout-settings-protocol-empty-group__drag-box' />
                <div className='graph-layout-settings-protocol-empty-group__hover-box flex items-center'>
                  <div className='graph-layout-settings-protocol-empty-group__color mr-lgu-3' />
                  <div className='mr-lgu-3 flex-1'>
                    {msg.canvasGraph.graphSidePanel.layoutSettings.protocol}
                  </div>
                  <i className='fa fa-arrows graph-layout-settings-protocol-empty-group__arrows' />
                </div>
              </div>
              <div className='flex-1 text-center'>
                {msg.canvasGraph.graphSidePanel.layoutSettings.emptyGroupTitle}
              </div>
            </div>
          ) : (
            <div className='pl-10'>
              <p className='pl-lgu-2'>
                {msg.canvasGraph.graphSidePanel.layoutSettings.noItems}
              </p>
            </div>
          ))}
      </div>
    </div>
  );
};

export const TopologyLayoutSettingsProtocolsGroup = memo(
  BareTopologyLayoutSettingsProtocolsGroup,
);
